import React, { useState, useEffect, useCallback } from 'react';
import { Gamepad2, X, ArrowRight, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export const LinkAccountModal: React.FC<{
  onAccountLinked?: (acc: { username: string; platform: string; is_primary: boolean }) => void;
}> = ({ onAccountLinked }) => {
  const isOpen = useUIStore((s) => s.isLinkAccountModalOpen);
  const close = useUIStore((s) => s.closeLinkAccountModal);

  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<'success' | 'warning' | 'error' | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll();
    setTimeout(() => {
      setIsClosing(false);
      close();
    }, 160);
  }, [isClosing, close]);

  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setStatusMessage(null);
      setStatusType(null);
      setIsClosing(false);
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => {
      unlockScroll();
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen && !isClosing) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = username.trim();
    if (!clean || clean.length < 3) {
      setStatusType('error');
      setStatusMessage('Ingresa tu nombre exacto de Epic Games (mínimo 3 caracteres).');
      return;
    }

    setLoading(true);
    setStatusMessage(null);
    setStatusType(null);

    try {
      let resolvedPlatform = 'Epic Games';
      let resolvedDisplayName = clean;
      let resolvedAccountId = '';
      let backendAccount: any = null;

      const token =
        localStorage.getItem('fn_token') ||
        localStorage.getItem('token');

      // 1. Intentar validar y persistir en el backend oficial si está autenticado
      if (token) {
        const res = await fetch('/api/auth/me/fortnite-accounts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ fortnite_username: clean }),
        });
        if (res.ok) {
          backendAccount = await res.json();
          if (backendAccount?.fortnite_username) {
            resolvedDisplayName = backendAccount.fortnite_username;
          }
          if (backendAccount?.fortnite_account_id) {
            resolvedAccountId = backendAccount.fortnite_account_id;
          }
        } else {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `No se encontró ningún jugador con el nombre '${clean}'.`);
        }
      } else {
        // Modo sin autenticar: validar mediante lookup-and-status
        const res = await fetch('/api/check/lookup-and-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: clean, total_vbucks: 1000 }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.user) {
            resolvedDisplayName = data.user.display_name || clean;
            resolvedAccountId = data.user.account_id || '';
            resolvedPlatform = data.user.platform_label || 'Epic Games';
          } else {
            throw new Error(`No se encontró ningún jugador con el nombre '${clean}'.`);
          }
        } else {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `No se encontró ningún jugador con el nombre '${clean}'.`);
        }
      }

      // 2. Persistir cuenta vinculada en localStorage preservando cuentas existentes
      const newAcc = {
        id: backendAccount?.id,
        username: resolvedDisplayName,
        account_id: resolvedAccountId,
        platform: resolvedPlatform,
        is_primary: Boolean(backendAccount?.is_default),
      };

      try {
        const stored = localStorage.getItem('fn_linked_accounts');
        let list: any[] = [];
        if (stored) {
          try {
            list = JSON.parse(stored);
          } catch {
            list = [];
          }
        }

        // Si la lista estaba vacía pero el usuario ya tenía cuenta principal registrada, preservarla
        const currentUser = useAuthStore.getState().user;
        if (list.length === 0 && currentUser?.fortnite_username) {
          list.push({
            username: currentUser.fortnite_username,
            platform: 'Epic Games',
            is_primary: true,
          });
        }

        if (
          !list.some(
            (a: any) =>
              a.username.toLowerCase() === resolvedDisplayName.toLowerCase()
          )
        ) {
          list.push(newAcc);
          localStorage.setItem('fn_linked_accounts', JSON.stringify(list));
        }
      } catch {}

      if (onAccountLinked) onAccountLinked(newAcc);
      window.dispatchEvent(new Event('accountsChanged'));

      // 3. Enviar solicitudes de amistad de bots en background con account_id requerido
      if (resolvedAccountId) {
        try {
          fetch('/api/check/send-all-bot-friend-requests', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: resolvedDisplayName,
              account_id: resolvedAccountId,
            }),
          }).catch(() => {});
        } catch {}
      }

      setStatusType('success');
      setStatusMessage(`¡Cuenta "${resolvedDisplayName}" vinculada con éxito!`);
      setTimeout(() => {
        setLoading(false);
        handleClose();
      }, 700);
    } catch (err: any) {
      setLoading(false);
      setStatusType('error');
      setStatusMessage(err.message || 'Error al conectar con los servidores.');
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain select-none ${
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      }`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="link-fn-title"
    >
      <div
        className={`w-full max-w-md bg-white text-[#06173f] p-5 sm:p-6 border-2 border-[var(--line)] shadow-2xl relative space-y-4 ${
          isClosing ? 'anim-modal-out' : 'anim-modal-in'
        }`}
        style={{
          clipPath:
            'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
          filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera idéntica a profile.html */}
        <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 bg-[var(--ice)] text-[var(--royal)] flex items-center justify-center font-bold text-base shrink-0"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <Gamepad2 className="w-5 h-5 text-[#1447e6]" />
            </div>
            <div>
              <h3
                id="link-fn-title"
                className="text-base sm:text-lg font-black uppercase text-[#06173f] font-heading tracking-wide leading-tight"
              >
                Vincular Usuario Fortnite
              </h3>
              <p className="text-xs text-[var(--soft)] font-medium">
                Para recibir regalos de nuestros bots
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-[var(--soft)] leading-relaxed text-left">
          Ingresa tu nombre exacto de Epic Games. El sistema verificará tu cuenta y nuestros bots enviarán solicitud de amistad automáticamente.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5 text-left">
            <label
              htmlFor="link-fn-input"
              className="text-xs font-bold text-[var(--soft)] uppercase tracking-wider block font-heading"
            >
              Nombre Visible (Display Name)
            </label>
            <div className="relative flex items-center">
              <div className="absolute left-3.5 flex items-center pointer-events-none text-[#1447e6]">
                <Gamepad2 className="w-4 h-4" />
              </div>
              <input
                id="link-fn-input"
                type="text"
                required
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (statusMessage) setStatusMessage(null);
                }}
                placeholder="Ej: Ninja, GamerLucas..."
                disabled={loading}
                className="w-full bg-white border-2 border-[var(--line)] py-3 pl-11 pr-4 text-sm font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition tracking-wide font-mono"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
              />
            </div>
          </div>

          {statusMessage && (
            <div
              className={`p-3 text-xs font-bold flex items-center gap-2 ${
                statusType === 'success'
                  ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
                  : 'bg-red-50 border border-red-200 text-red-600'
              }`}
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
              }}
            >
              {statusType === 'success' && (
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              )}
              <span>{statusMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !username.trim()}
            className="btn w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
            style={{
              clipPath:
                'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              color: '#ffffff',
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span className="text-white">Buscando cuenta...</span>
              </>
            ) : (
              <>
                <span className="text-white">Buscar y Vincular Cuenta</span>
                <ArrowRight className="w-4 h-4 text-white" />
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-center text-[var(--soft)] font-medium flex items-center justify-center gap-1.5 pt-1">
          <ShieldCheck className="w-4 h-4 text-[#1447e6]" />
          <span>Nuestros bots enviarán solicitud de amistad automáticamente.</span>
        </p>
      </div>
    </div>
  );
};

export default LinkAccountModal;
